/**
 * The applications a card can be referenced by, and the design-system chip tone
 * each one uses (spec R18).
 *
 * The prototype's raw hexes (#030562 / #C26500 / #10A050) are replaced by this
 * repo's own token families per the tickets' "replace anything that deviates
 * from the design system" note. The colours themselves stay the ones R18 names
 * — Guide = blue, Commend = orange, Resolve = green — so the Chip primitive
 * carries an `orange` tone rather than the module bending Commend towards the
 * Figma set's nearest neighbour. The badge always carries the application
 * *name* as text, which is the non-colour cue colour alone would not provide
 * (NFR A1).
 */

import { TagTone } from 'types/DesignSystem/Tag';
import { ApplicationRef } from '../types';

export interface ApplicationDefinition {
  id: ApplicationRef;
  label: string;
  tone: TagTone;
}

export const APPLICATIONS: ApplicationDefinition[] = [
  { id: 'guide', label: 'Guide', tone: 'info' },
  { id: 'commend', label: 'Commend', tone: 'warning' },
  { id: 'resolve', label: 'Resolve', tone: 'success' },
];

const APPLICATIONS_BY_ID: Map<ApplicationRef, ApplicationDefinition> = new Map(
  APPLICATIONS.map((application) => [application.id, application])
);

export const isApplicationRef = (candidate: unknown): candidate is ApplicationRef =>
  typeof candidate === 'string' && APPLICATIONS_BY_ID.has(candidate as ApplicationRef);

export const getApplication = (id: ApplicationRef): ApplicationDefinition => {
  const application = APPLICATIONS_BY_ID.get(id);
  if (!application) {
    throw new Error(`Unknown referencing application: ${String(id)}`);
  }
  return application;
};

export const getApplicationLabel = (id: ApplicationRef): string => getApplication(id).label;
